import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  HomeIcon,
  CalendarIcon,
  UserGroupIcon,
  ChartBarIcon,
  CogIcon,
  Bars3Icon,
  XMarkIcon,
  PresentationChartLineIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '../hooks/useAuth'
import { ANALYTICS_CAPABLE_ROLES } from '../hooks/useAnalyticsSummary'
import DemoBanner from '../demo/DemoBanner'

interface LayoutProps {
  children: React.ReactNode
}

interface NavItem {
  name: string
  href: string
  icon: typeof HomeIcon
  /** When set, only these roles see the item. Absent means everyone sees it. */
  roles?: ReadonlySet<string>
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  { name: 'Events', href: '/events', icon: CalendarIcon },
  { name: 'Athletes', href: '/athletes', icon: UserGroupIcon },
  { name: 'Results', href: '/results', icon: ChartBarIcon },
  {
    name: 'Analytics',
    href: '/analytics',
    icon: PresentationChartLineIcon,
    // /api/analytics/events/:id 403s every other role, so the link would only
    // ever lead to a permissions notice. The page states that plainly if
    // someone reaches it by URL; this just stops us inviting the click.
    roles: ANALYTICS_CAPABLE_ROLES,
  },
  { name: 'Settings', href: '/settings', icon: CogIcon },
]

export default function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { user, logout } = useAuth()
  const location = useLocation()

  // Until the user resolves, show the unrestricted items only — better a link
  // that appears a moment late than one that flashes and vanishes.
  const visibleNavigation = navigation.filter(
    item => !item.roles || (user?.role !== undefined && item.roles.has(user.role))
  )

  const isCurrentPage = (href: string) => {
    return location.pathname === href
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
          <div className="flex items-center">
            <img src="/ecombine-logo.svg" alt="eCombine" className="h-8" />
            <span className="ml-2 text-xl font-semibold text-gray-900">eCombine</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <nav className="mt-8 px-4 space-y-2">
          {visibleNavigation.map(item => (
            <Link
              key={item.name}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                isCurrentPage(item.href)
                  ? 'bg-primary-100 text-primary-900'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <item.icon
                className={`mr-3 h-5 w-5 ${
                  isCurrentPage(item.href)
                    ? 'text-primary-500'
                    : 'text-gray-400 group-hover:text-gray-500'
                }`}
              />
              {item.name}
            </Link>
          ))}
        </nav>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <div className="flex items-center h-16 px-4 border-b border-gray-200">
            <img src="/ecombine-logo.svg" alt="eCombine" className="h-8" />
            <span className="ml-2 text-xl font-semibold text-gray-900">eCombine</span>
          </div>
          <nav className="mt-8 flex-grow px-4 space-y-2">
            {visibleNavigation.map(item => (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                  isCurrentPage(item.href)
                    ? 'bg-primary-100 text-primary-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <item.icon
                  className={`mr-3 h-5 w-5 ${
                    isCurrentPage(item.href)
                      ? 'text-primary-500'
                      : 'text-gray-400 group-hover:text-gray-500'
                  }`}
                />
                {item.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col flex-1">
        <DemoBanner />
        {/* Top header */}
        <div className="sticky top-0 z-10 bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-gray-500 hover:text-gray-600 lg:hidden"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>

            <div className="flex items-center space-x-4">
              <div className="text-sm">
                <span className="text-gray-500">Welcome back,</span>
                <span className="ml-1 font-medium text-gray-900">
                  {user?.profile?.firstName || user?.email}
                </span>
              </div>
              <button onClick={logout} className="text-sm text-gray-500 hover:text-gray-700">
                Sign Out
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
          </div>
        </main>
      </div>
    </div>
  )
}
